import { useState, useEffect, DragEvent } from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  ActiveReportItem,
  ReportsList,
  ReportLabel,
  DragHandle,
  AddReportContainer,
  NoReportsMessage,
  ReportItemStyled,
  ReportItemContent,
} from './styled/ReportSelector.styled';
import { Sortable, Close } from '../../juristec-ui/icons';
import Button from '../../juristec-ui/core/Button';
import Select from '../../juristec-ui/core/SelectNew';
import IconButton from '../../juristec-ui/core/IconButton';

interface ReportOption {
  id: string;
  label: string;
  value: string;
}

interface ReportSelectorProps {
  reportIds: string[];
  activeReportId: string;
  availableReports: ReportOption[];
  onChange: (reportIds: string[]) => void;
  atModal?: boolean;
}

const ReportSelector = ({
  reportIds,
  activeReportId,
  availableReports,
  onChange,
  atModal = false,
}: ReportSelectorProps) => {
  const [orderedReports, setOrderedReports] = useState<ReportOption[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [showAddReport, setShowAddReport] = useState(false);

  // Initialize ordered reports
  useEffect(() => {
    const activeReport = availableReports.find((r) => r.id === activeReportId);
    const otherReports = reportIds
      .filter((id) => id !== activeReportId)
      .map((id) => availableReports.find((r) => r.id === id))
      .filter(Boolean) as ReportOption[];

    const ordered: ReportOption[] = [];
    if (activeReport) {
      ordered.push(activeReport);
    }
    ordered.push(...otherReports);

    setOrderedReports(ordered);
  }, [reportIds, activeReportId, availableReports]);

  const handleDragStart = (index: number) => {
    if (index === 0) return;
    setDraggedIndex(index);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>, index: number) => {
    if (index === 0) return;
    e.preventDefault();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>, dropIndex: number) => {
    if (dropIndex === 0 || draggedIndex === null || draggedIndex === 0) return;
    e.preventDefault();

    const newReports = [...orderedReports];
    const draggedReport = newReports[draggedIndex];
    newReports.splice(draggedIndex, 1);
    newReports.splice(dropIndex, 0, draggedReport);

    setOrderedReports(newReports);
    setDraggedIndex(null);

    // Only pass dependency reports (exclude activeReportId)
    const dependencyReportIds = newReports.map((r) => r.id).filter((id) => id !== activeReportId);
    onChange(dependencyReportIds);
  };

  const handleRemoveReport = (index: number) => {
    if (index === 0) return;

    const newReports = orderedReports.filter((_, i) => i !== index);
    setOrderedReports(newReports);
    // Only pass dependency reports (exclude activeReportId)
    const dependencyReportIds = newReports.map((r) => r.id).filter((id) => id !== activeReportId);
    onChange(dependencyReportIds);
  };

  const handleAddReport = (selectedReports: ReportOption[]) => {
    const newReportIds = orderedReports.map((r) => r.id);

    selectedReports.forEach((report) => {
      if (report.id !== activeReportId && !newReportIds.includes(report.id)) {
        newReportIds.push(report.id);
      }
    });

    const newOrderedReports = availableReports
      .filter((r) => newReportIds.includes(r.id))
      .sort((a, b) => {
        if (a.id === activeReportId) return -1;
        if (b.id === activeReportId) return 1;
        return newReportIds.indexOf(a.id) - newReportIds.indexOf(b.id);
      });

    setOrderedReports(newOrderedReports);
    // Only pass dependency reports (exclude activeReportId)
    const dependencyReportIds = newReportIds.filter((id) => id !== activeReportId);
    onChange(dependencyReportIds);
    setShowAddReport(false);
  };

  const getAvailableReportsForAdd = () => availableReports.filter(
    (report) => report.id !== activeReportId && !orderedReports.some((r) => r.id === report.id),
  );

  return (
    <Container>
      {orderedReports.length === 0 ? (
        <NoReportsMessage>
          Nenhum relatório selecionado. Clique em &quot;Adicionar Relatório&quot; para
          começar.
        </NoReportsMessage>
      ) : (
        <>
          {orderedReports[0] && (
            <ActiveReportItem>
              <ReportItemContent>
                <ReportLabel>{orderedReports[0].label}</ReportLabel>
                <span style={{ fontSize: '12px', color: 'rgb(150, 150, 150)' }}>
                  (Relatório atual - Obrigatório)
                </span>
              </ReportItemContent>
            </ActiveReportItem>
          )}

          {orderedReports.length > 1 && (
            <ReportsList>
              {orderedReports.map((report, index) => {
                if (index === 0) return null;

                return (
                  <ReportItemStyled
                    key={report.id}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={() => setDraggedIndex(null)}
                    isDragging={draggedIndex === index}
                  >
                    <DragHandle>
                      <Sortable />
                    </DragHandle>
                    <ReportItemContent>
                      <ReportLabel>{report.label}</ReportLabel>
                      <span style={{ fontSize: '12px', color: 'rgb(150, 150, 150)' }}>
                        Índice:
                        {' '}
                        {index}
                      </span>
                    </ReportItemContent>
                    <IconButton
                      onClick={() => handleRemoveReport(index)}
                      title="Remover relatório"
                    >
                      <Close />
                    </IconButton>
                  </ReportItemStyled>
                );
              })}
            </ReportsList>
          )}
        </>
      )}

      <AddReportContainer>
        {!showAddReport ? (
          <Button
            onClick={() => setShowAddReport(true)}
            variant="outlined"
            size="small"
            style={{ width: '100%' }}
          >
            Adicionar Relatório
          </Button>
        ) : (
          <div style={{ width: '100%' }}>
            <Select
              label="Selecione relatórios para adicionar"
              options={getAvailableReportsForAdd()}
              onChange={handleAddReport}
              multiple
              stayOpen
              searchable
              clearable
              atModal={atModal}
              sortBy="ascending"
              placeholder="Selecione os relatórios"
              autoFocus
            />
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <Button
                onClick={() => setShowAddReport(false)}
                variant="outlined"
                size="small"
                style={{ flex: 1 }}
              >
                Cancelar
              </Button>
            </div>
          </div>
        )}
      </AddReportContainer>
    </Container>
  );
};

ReportSelector.propTypes = {
  reportIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeReportId: PropTypes.string.isRequired,
  availableReports: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  atModal: PropTypes.bool,
};

export default ReportSelector;
